<template>
  <div class="relative">
    <ConnectionInform />
    <div class="overlay-disable-canvas" v-if="isDisableCanvas"></div>
    <SlideTitle
      v-if="isSlideTitleComponentVisible"
      :showSoloRanking="showSoloRanking"
      @hideQuestionImage="hideQuestionImage"
    />

    <div
      class="text-center text-no-answer-correct"
      v-if="showTextNoneIsCorrect"
    >
      {{ $t('None of the options is correct!') }}
    </div>

    <!-- Canvas content -->
    <div class="flex canvas-wrapper">
      <div class="canvas-content px-8" v-if="isOpenEndedSlide && isInternalUser">
        <OpenEndedContent class="pt-16" />
      </div>
      <div class="canvas-content" v-if="isOpenEndedSlide && !isInternalUser">
        <OpenEndedContent />
      </div>
      <div class="canvas-content canvas-textSlide" v-if="slideActive.type == 'textSlide'">
        <TextSlideContent />
      </div>

      <div class="canvas-content canvas-textSlide" v-if="slideActive.type == 'document'">
        <DocumentContent />
      </div>
      <div class="canvas-content canvas-bullet-points" v-if="slideActive.type == 'bulletPoints'">
        <BulletPointsContent />
      </div>

      <div class="canvas-content horizontal layout center-justified" v-if="isQrCodeSlide">
        <QRCodeContent />
      </div>
      <div
        class="canvas-content horizontal layout center-justified"
        :class="{ 'center': isSlideImageInDefaultMode }"
        v-if="isImageSlide"
      >
        <ImageContent />
      </div>

      <div
        class="canvas-content horizontal layout center center-justified"
        v-if="isYouTubeSlide"
      >
        <YoutubeContent />
      </div>

      <div
        class="pb-2 canvas-content horizontal layout end center-justified"
        v-if="isGoogleSlidesContentVisible"
      >
        <GoogleSlidesContent />
      </div>

      <div
        class="canvas-content horizontal layout center center-justified"
        v-if="isWordCloudSlide"
      >
        <WordCloudContentV2 v-if="canUseWordCloudEmqx" />
        <WordCloudContent v-else />
      </div>

      <div class="canvas-content h-full" v-if="isCategoriseSlide">
        <div v-if="isPresentingOrBackstage && !sharePresentation" class="full-size">
          <QuizSlideContent>
            <CategoriseContent hideBlankCategories />
          </QuizSlideContent>
        </div>

        <div v-else class="h-full">
          <CategoriseContent />
        </div>
      </div>

      <div
        class="canvas-content horizontal layout center center-justified"
        v-if="isQnASlide"
      >
        <QuestionContent />
      </div>

      <div
        class="canvas-content horizontal layout center center-justified"
        v-if="isBrainstormSlide"
      >
        <VotingIdeasContentWrapper />
      </div>
      <div class="canvas-content p-3" v-if="isPollSlide">
        <PollContentV2
          v-if="canUsePollEmqx"
          @updateLayout="updateQuestionImageLayout"
        />
        <PollContentWrapper
          v-else
          @updateLayout="updateQuestionImageLayout"
        />
      </div>
      <div class="canvas-content" v-if="isScalesSlide">
        <ScaleQuestionV2 v-if="canUseScaleEmqx" />
        <ScaleQuestion v-else />
      </div>

      <div class="canvas-content" v-if="isPickAnswerSlideType">
        <div class="full-size" v-if="isPresentingOrBackstage && !sharePresentation">
          <QuizSlideContent />
        </div>
        <div v-if="sharePresentation || !isPresentingOrBackstage" class="full-size px-3">
          <CanvasHints v-if="isCanvasHintsVisible" />
          <SlideContentWrapper />
        </div>
      </div>

      <div class="canvas-content" v-if="isPickImageSlideType">
        <PickAnswerV2
          v-if="canMergePickImageIntoPickAnswer"
          :sharePresentation="sharePresentation"
          :isCanvasHintsVisible="isCanvasHintsVisible"
          :isPresentingOrBackstage="isPresentingOrBackstage"
        />
        <PickAnswer
          v-else
          :isPresentingOrBackstage="isPresentingOrBackstage"
          :isCanvasHintsVisible="isCanvasHintsVisible"
          :sharePresentation="sharePresentation"
        />
      </div>

      <div class="canvas-content" v-if="isTypeAnswerSlideType">
        <div class="full-size" v-if="isPresentingOrBackstage && !sharePresentation">
          <QuizSlideContent />
        </div>
        <div v-if="sharePresentation || !isPresentingOrBackstage" class="full-size px-3">
          <SlideContentWrapper />
        </div>
      </div>

      <div class="canvas-content" v-if="isMatchPairsSlideType">
        <div v-if="isPresentingOrBackstage && !sharePresentation" class="full-size">
          <QuizSlideContent />
        </div>

        <div v-else class="full-size horizontal layout center center-justified">
          <MatchPairsContent />
        </div>
      </div>

      <!-- Correct Order Content -->
      <div class="canvas-content" v-if="isCorrectOrderSlideType">
        <div v-if="isPresentingOrBackstage && !sharePresentation" class="full-size">
          <QuizSlideContent />
        </div>

        <div v-else class="full-size horizontal layout center center-justified">
          <CorrectOrderContent />
        </div>
      </div>

      <div class="canvas-content" v-if="isLeaderboardSlide">
        <LeaderboardContent
          v-if="(!presentation.teamPlay || showSoloRanking)"
          @toggleShowCarousel="toggleShowCarousel"
          @showWinner="handleShowWinner"
          :show-carousel="showCarouselSolo"
        />
        <LeaderboardTeamContent
          v-if="presentation.teamPlay && !showSoloRanking"
          @toggleShowCarousel="toggleShowCarousel"
          @showWinner="handleShowWinner"
          :show-carousel="showCarouselTeam"
        />
      </div>

      <div class="canvas-content" v-if="isSpinnerWheelSlide">
        <SpinnerWheelCanvas />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import QuizStatuses from '@/constant/QuizStatuses'
import SlideTitle from '@/components-v2/presentation-editor/middle/SlideTitle.vue'
import OpenEndedContent from '@/components/presentations/contents/OpenEndedContent.vue'
import TextSlideContent from '@/components/presentations/contents/TextSlideContent.vue'
import BulletPointsContent from '@/components/presentations/contents/BulletPointsContent.vue'
import ImageContent from '@/components/presentations/contents/ImageContent.vue'
import QRCodeContent from '@/components/presentations/contents/QRCodeContent.vue'
import DocumentContent from '@/components/presentations/contents/DocumentContent.vue'
import GoogleSlidesContent from '@/components/presentations/contents/GoogleSlidesContent.vue'
import YoutubeContent from '@/components/presentations/contents/YoutubeContent.vue'
import WordCloudContent from '@/components/presentations/contents/WordCloudContent.vue'
import WordCloudContentV2 from '@/components/presentations/contents/WordCloudSlideContent.v2.vue'
import QuizSlideContent from '@/components/presentations/contents/QuizSlideContent.vue'
import LeaderboardContent from '@/components/presentations/contents/LeaderboardContent.vue'
import LeaderboardTeamContent from '@/components/presentations/contents/LeaderboardTeamContent.vue'
import QuestionContent from '@/components/presentations/contents/QuestionContent.vue'
import MatchPairsContent from '@/components-v2/presentation-editor/match-pairs/MatchPairsContent.vue'
import CategoriseContent from '@/components/presentations/contents/categorise/CategoriseContent'
import CorrectOrderContent from '@/components-v2/presentation-editor/correct-order/CorrectOrderContent.vue'
import VotingIdeasContentWrapper from '@/components/presentations/contents/VotingIdeasContentWrapper.vue'
import PollContentWrapper from '@/components/presentations/contents/PollContentWrapper.vue'
import PollContentV2 from '@/components/presentations/contents/PollContentV2.vue'
import SpinnerWheelCanvas from '@/components-v2/presentation-editor/spinner-wheel-canvas/SpinnerWheelCanvas.vue'
import CanvasHints from '@/components-v2/presentation-editor/middle/hints/CanvasHints.vue'
import ConnectionInform from '@/components-v2/presentation-editor/middle/connection/ConnectionInform.vue'

// charts
import ScaleQuestion from '@/components/presentations/charts/ScaleQuestion.vue'
import ScaleQuestionV2 from '@/components-v2/presentation-editor/middle/scale/ScaleQuestionV2.vue'
import SlideContentWrapper from '@/components-v2/presentation-editor/middle/common/SlideContentWrapper.vue'
import PickAnswer from '@/components/presentations/charts/PickAnswer.vue'
import PickAnswerV2 from '@/components-v2/presentation-editor/middle/pick-answer/PickAnswerV2.vue'

export default {
  components: {
    SlideTitle,
    OpenEndedContent,
    TextSlideContent,
    BulletPointsContent,
    ImageContent,
    QRCodeContent,
    DocumentContent,
    GoogleSlidesContent,
    YoutubeContent,
    WordCloudContent,
    WordCloudContentV2,
    QuizSlideContent,
    LeaderboardContent,
    LeaderboardTeamContent,
    QuestionContent,
    MatchPairsContent,
    CorrectOrderContent,
    VotingIdeasContentWrapper,
    PollContentWrapper,
    PollContentV2,
    SpinnerWheelCanvas,
    CanvasHints,
    CategoriseContent,
    ConnectionInform,

    // charts
    ScaleQuestion,
    ScaleQuestionV2,
    SlideContentWrapper,
    PickAnswer,
    PickAnswerV2,
  },
  props: {
    showSoloRanking: {
      type: Boolean,
      default: false,
    },
    showCarouselSolo: {
      type: Boolean,
      default: false,
    },
    showCarouselTeam: {
      type: Boolean,
      default: false,
    },
    showWinnerId: {
      type: Number,
      default: null,
    },
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapGetters('liveStatsV2', [
      'canUseWordCloudEmqx',
      'canUsePollEmqx',
      'canUseScaleEmqx',
    ]),
    ...mapGetters('featureToggles', [
      'canMergePickImageIntoPickAnswer',
    ]),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      sharePresentation: (state) => state.sharePresentation,
      isPlayDemoMode: (state) => state.isPlayDemoMode,
      isScreenCapture: (state) => state.isScreenCapture,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      canEditPresentation: (state) => state.canEditPresentation,
      currentUser: (state) => state.currentUser,
    }),
    isDisableCanvas() {
      if (!this.canEditPresentation) {
        // in-case viewer is presenting
        if (!this.isDisableEditPresentation && this.presentation.presenting) {
          return false
        }
        return true
      }
      return false
    },
    isSlideTitleComponentVisible() {
      const slideTypes = ['googleSlides', 'textSlide', 'bulletPoints', 'freestyle']
      return !slideTypes.includes(this.slideActive.type)
    },
    hasClassHiddenAudio() {
      const { type, quizStatus } = this.slideActive
      return this.isPresenting && type == 'pickAnswer' && quizStatus < QuizStatuses.COUNTDOWN_5S_SCREEN && !this.sharePresentation
    },
    showTextNoneIsCorrect() {
      const exceptSlideTypes = ['matchPairs', 'correctOrder', 'categorise']
      if (this.slideActive.type === 'pickAnswer'
        && exceptSlideTypes.includes(this.slideActive.slideType)) return false
      return this.slideActive.showCorrectOption
        && this.slideActive.addCorrectOption
        && !this.checkAnswerCorrect()
        && !this.slideActive.showQuestionImage
        && !this.slideActive.hideResult
        && !this.slideActive.showAcceptedAnswerView
        && (this.slideActive.type === 'multipleChoice' || this.slideActive.type === 'imageChoice'
          || (this.slideActive.type === 'pickAnswer' && this.isQuizResultScreen && this.isPresenting)
        )
    },
    isPollSlide() {
      const slideTypes = ['multipleChoice', 'imageChoice']
      return slideTypes.includes(this.slideActive.type)
    },
    isLeaderboardSlide() {
      return this.slideActive.id && this.slideActive.type === 'leaderboard'
    },
    isOpenEndedSlide() {
      return this.slideActive.id && this.slideActive.type === 'openEnded'
    },
    isWordCloudSlide() {
      return this.slideActive.id && this.slideActive.type === 'wordCloud'
    },
    isCategoriseSlide() {
      return this.slideActive.id && this.slideActive.slideType === 'categorise'
    },
    isQnASlide() {
      return this.slideActive.id && this.slideActive.type === 'question'
    },
    isBrainstormSlide() {
      return this.slideActive.id && this.slideActive.type === 'ideas'
    },
    isScalesSlide() {
      return this.slideActive.id && this.slideActive.type === 'scale'
    },
    isQrCodeSlide() {
      return this.slideActive.id && this.slideActive.type === 'qrcode'
    },
    isSpinnerWheelSlide() {
      return this.slideActive.id && this.slideActive.type === 'spinnerWheel'
    },
    isImageSlide() {
      return this.slideActive.id && this.slideActive.type === 'image'
    },
    isYouTubeSlide() {
      return this.slideActive.id && this.slideActive.type === 'youtube'
    },
    isFreestyleType() {
      return this.slideActive.id && this.slideActive.type === 'freestyle'
    },
    isQuizSlideType() {
      const { id, type } = this.slideActive
      return id && type == 'pickAnswer'
    },
    isPickAnswerSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && !slideType
    },
    isPickImageSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && slideType == 'imageChoice'
    },
    isTypeAnswerSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && slideType === 'typeAnswer'
    },
    isMatchPairsSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && slideType === 'matchPairs'
    },
    isCorrectOrderSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && slideType === 'correctOrder'
    },
    isPresentingOrBackstage() {
      return this.isPresenting || this.isBackstage
    },
    isSlideImageInDefaultMode() {
      return this.slideActive.imageType == 'default' && this.slideActive.image
    },
    isGoogleSlidesContentVisible() {
      const { id, type, publishedLink } = this.slideActive
      return id && type == 'googleSlides' && publishedLink
    },
    isCanvasHintsVisible() {
      return this.slideActive.isHintsVisible
        && !this.presentation.audiencePacing && !this.isSlideActiveHasResult
    },
    isQuizResultScreen() {
      return this.slideActive.quizStatus === QuizStatuses.RESULT_SCREEN
    },
    isSlideActiveHasResult() {
      const { SlideOptions } = this.slideActive
      return SlideOptions && SlideOptions.length && SlideOptions.some((option) => option.count > 0)
    },
    isInternalUser() {
      return this.currentUser && this.currentUser.email &&
        (this.currentUser.email.includes('@a.com') ||
        this.currentUser.email.includes('@ahaslides.com'))
    },

  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    hideQuestionImage() {
      const { showQuestionImage } = this.slideActive
      this.updateSlideActiveAction({
        showQuestionImage: !showQuestionImage,
      })
      if (this.sharePresentation) {
        return
      }
      this.$emit('updateSlide', {
        accessCode: this.presentation.uniqueAccessCode,
        showQuestionImage: this.slideActive.showQuestionImage,
      }, 4)
    },
    updateQuestionImageLayout(mode) {
      if (this.sharePresentation) {
        return
      }
      this.updateSlideActiveAction({
        questionImageLayout: mode,
      })
      this.$emit('updateSlide', {
        accessCode: this.presentation.uniqueAccessCode,
        questionImageLayout: mode,
      }, 4)
    },
    checkAnswerCorrect() {
      if (this.slideActive.type == 'pickAnswer' && this.slideActive.slideType == 'typeAnswer') {
        if (this.slideActive.correctQuizTypeAnswer) {
          return true
        }
        if (this.slideActive.otherCorrectQuiz && this.slideActive.otherCorrectQuiz.length) {
          return true
        }
      }
      for (
        let i = 0;
        i < this.slideActive.SlideOptions.length;
        i++
      ) {
        if (this.slideActive.SlideOptions[i].correct) {
          return true
        }
      }
      return false
    },

    toggleShowCarousel(type, id) {
      this.$emit('toggleShowCarousel', type, id)
    },

    handleShowWinner(id) {
      this.$emit('handleShowWinner', id)
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay-disable-canvas {
  z-index: 200;
  @apply bg-base-0 opacity-0 absolute left-0 top-0 w-full h-full;
}
.audio-control-slide.center-align {
  text-align: center;
  .audio-control {
    -moz-justify-content: center;
    justify-content: center;
  }
}
.audio-control-slide.is-exporting-pdf {
  display: none;
}
.canvas-wrapper {
  height: 100%;
  width: 100%;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  .canvas-content {
    height: 100%;
    width: 100%;
  }
}
.canvas-textSlide {
  width: 82%;
  margin: 0 auto;
}
.canvas-bullet-points {
  width: 100%;
  height: 100%;
}
</style>
